'use client'

import { useMemo, useState } from 'react'
import { FontAwesomeSvgIcon } from 'react-fontawesome-svg-icon'

import { faGauge, faGaugeMax, faGaugeMin } from '@fortawesome/pro-light-svg-icons'
import {
  Bar,
  Cell,
  ComposedChart,
  Legend,
  Rectangle,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts'
import { ContentType } from 'recharts/types/component/DefaultLegendContent'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { Props } from 'recharts/types/shape/Rectangle'
import { theme } from 'tailwind.config.js'

import { AnalyticsQueryDatapointEnum, AnalyticsQueryQueryEnum, UsageResult } from '@infura/packages.api'
import { getUserRequestThreshold } from '@infura/packages.common/user'
import { Text } from '@infura/packages.ui/Text'

import { DailyCreditUsageEmpty } from 'components/dashboard/analytics-overview/DailyCreditUsageChart/DailyCreditUsageEmpty'
import { DailyCreditUsageSkeleton } from 'components/dashboard/analytics-overview/DailyCreditUsageChart/DailyCreditUsageSkeleton'
import { TooltipContent } from 'components/dashboard/analytics-overview/DailyCreditUsageChart/TooltipContent'

import { useDynamicStats1_5_Query } from 'hooks/useStats'
import { useUKSServicePlan } from 'hooks/useUKSServicePlan'
import { useUsage } from 'hooks/useUsage'

import { serializer } from 'lib/analytics-overview/serializer'
import { CHART_COLORS } from 'lib/constants'
import dayjs from 'lib/dayjs'
import { InfuraStats } from 'lib/stats/stats-types'
import { getFormattedUsage, getTotalUsage } from 'lib/usage/getTotalUsage'

export const backgroundColor = theme.extend.colors.gray['700']
export const thresholdColor = theme.extend.colors.gray['300']

interface DailyCreditUsageChartProps {
  groupedSeriesName?: string
  isUsageEnabled: boolean
  creditLimit?: number
}

export const THRESHOLD_NAMES = ['seventyFivePercent', 'eightyFivePercent'] as const

export type ThresholdName = (typeof THRESHOLD_NAMES)[number]

export const BAR_HEIGHT = 20
const TEN_SECONDS = 10_000

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ThresholdShape = ({ height, key, width, y, ...rest }: Props) => (
  <Rectangle height={BAR_HEIGHT} key={`te-${key}`} width={4} y={(y as number) - 6} {...rest} />
)

const getThresholdIcon = (percentUsed: number, className?: string) => {
  if (percentUsed < 75) {
    return <FontAwesomeSvgIcon className={className} icon={faGaugeMin} size='sm' />
  }
  if (percentUsed < 85) {
    return <FontAwesomeSvgIcon className={className} icon={faGauge} size='sm' />
  }
  return <FontAwesomeSvgIcon className={className} icon={faGaugeMax} size='sm' />
}

export const DailyCreditUsageChart = ({
  creditLimit,
  groupedSeriesName = 'all other',
  isUsageEnabled
}: DailyCreditUsageChartProps) => {
  const [focusSeries, setFocusSeries] = useState<string | null>(null)

  const { servicePlan } = useUKSServicePlan()
  const reqLimit = useMemo(() => getUserRequestThreshold(servicePlan), [servicePlan])

  const limit = (isUsageEnabled ? creditLimit : reqLimit) as number

  const { endOfDay, startOfDay } = useMemo(() => {
    const now = dayjs().utc()
    const startOfDay = now.startOf('day')
    const endOfDay = now.endOf('day')

    return { endOfDay, startOfDay }
  }, [])

  // Creating an array with a placeholder color for the background item
  const chartColorsWithBackground = ['#d4d4d4', ...CHART_COLORS] as Array<string>

  const {
    data: statsData,
    error,
    isLoading
  } = useDynamicStats1_5_Query({
    config: {
      refreshInterval: TEN_SECONDS
    },
    datapoint: AnalyticsQueryDatapointEnum.COUNT,
    intervals: `${startOfDay.toISOString()}/${endOfDay.toISOString()}`,
    product: 'ethereum',
    projectIds: [],
    query: AnalyticsQueryQueryEnum.METHOD_DOMINANCE,
    time: '24h'
  })

  const {
    error: usageError,
    isLoading: isLoadingUsage,
    totalUsageData
  } = useUsage({
    intervals: `${startOfDay.toISOString()}/${endOfDay.toISOString()}`,
    method: 'method_usage',
    refreshInterval: TEN_SECONDS
  })

  const chartData = serializer({
    isUsageEnabled,
    quota: limit,
    rawData: isUsageEnabled
      ? (totalUsageData?.data[0]?.result as InfuraStats.MethodVolumeRawData)
      : (statsData?.data[0]?.result as InfuraStats.MethodVolumeRawData)
  })

  const renderLegend: ContentType = useMemo(() => {
    const bgClassNames = CHART_COLORS.map(color => `h-4 w-4 bg-[${color}] rounded-sm`)

    // eslint-disable-next-line react/display-name
    return ({ payload }) => {
      // remove the first item which is the background
      const cleanedPayload = payload?.slice(1)
      return (
        <div
          className='border-border-muted text-text-default flex max-w-[1100px] flex-wrap gap-x-8 gap-y-4 rounded-[5px] border px-4 py-2'
          data-testid='daily-credit-top-methods'
        >
          {cleanedPayload?.map((entry, index) => {
            if (!chartData.groupedItems.includes(entry.value) && !THRESHOLD_NAMES.includes(entry.value)) {
              return (
                <div
                  className='flex items-center gap-1'
                  data-entry-value={entry.value}
                  data-testid='daily-credit-filter-item'
                  key={`item-${entry.value}`}
                >
                  <div className={bgClassNames[index]} />

                  <div className='max-w-[100px] overflow-hidden text-ellipsis'>{entry.value}</div>
                </div>
              )
            }
          })}
        </div>
      )
    }
  }, [chartData])

  const CustomTooltip = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      ({ active, payload }: TooltipProps<ValueType, NameType>) => {
        if (
          active &&
          payload?.length &&
          (chartData.labels.find(({ text }) => text === focusSeries) ||
            THRESHOLD_NAMES.includes(focusSeries as ThresholdName))
        ) {
          return <TooltipContent focusSeries={focusSeries} isUsageEnabled={isUsageEnabled} payload={payload} />
        }
        return null
      },
    [focusSeries, chartData, isUsageEnabled]
  )

  if (isUsageEnabled ? error : usageError) {
    return <p>There was an error loading the chart, please try refreshing the page</p>
  }

  if (isUsageEnabled ? isLoadingUsage : isLoading) {
    return <DailyCreditUsageSkeleton />
  }

  if (isUsageEnabled ? !totalUsageData?.data[0]?.result?.length : !statsData?.data[0]?.result?.length) {
    return <DailyCreditUsageEmpty limit={limit} />
  }

  const totalUsage = getTotalUsage({
    data: isUsageEnabled ? totalUsageData?.data[0]?.result : (statsData?.data[0]?.result as Array<UsageResult>),
    mode: isUsageEnabled ? 'usage' : 'stats'
  })

  return (
    <div className='flex h-full flex-col justify-end py-6'>
      <div>
        <div className='flex justify-between'>
          <div data-testid='daily-credit-usage-value'>
            <Text as='span' className='text-2xl font-bold'>
              {getFormattedUsage(totalUsage?.value || 0)}
            </Text>
            <Text alternative={true} as='span'>
              {' '}
              of {getFormattedUsage(limit)} used
            </Text>
          </div>
          <div className='text-text-alternative flex items-center gap-1'>
            <Availability limit={limit} totalUsage={totalUsage?.value ?? 0} />
          </div>
        </div>
        <div className='xs:h-[250px] desktop:h-[190px] wide:h-[140px] xxl:h-[100px] h-[380px] sm:h-[200px] md:h-[230px]'>
          <ResponsiveContainer>
            <ComposedChart data={chartData?.timeseries} layout='vertical'>
              <YAxis dataKey='date' hide type='category' />
              <XAxis axisLine={false} domain={[0, limit]} hide type='number' />
              <Tooltip
                content={<CustomTooltip />}
                cursor={false}
                isAnimationActive={false}
                position={{ y: focusSeries && THRESHOLD_NAMES.includes(focusSeries as ThresholdName) ? 0 : -150 }}
              />
              <Legend align='left' content={renderLegend} verticalAlign='bottom' />
              {/* add a background item to the payload so on background hover the tooltip is not visible */}
              {[
                {
                  dataKey: '',
                  text: 'background',
                  tooltipText: 0
                },
                ...chartData.labels
              ].map((label, index) => {
                if (!chartData.groupedItems.includes(label.text)) {
                  return (
                    <Bar
                      barSize={BAR_HEIGHT}
                      dataKey={label.text}
                      fill={chartColorsWithBackground[index] as string}
                      key={label.text}
                      stackId='a'
                      {...(index === 0 && { background: { fill: backgroundColor, radius: 2 } })}
                      {...(index === 0 && { onMouseEnter: () => focusSeries && setFocusSeries(null) })}
                    >
                      {chartData.timeseries.map((entry, cellIndex) => {
                        return (
                          <Cell
                            fill={
                              groupedSeriesName === label.text.toLowerCase()
                                ? (chartColorsWithBackground.at(-1) as string)
                                : (chartColorsWithBackground[index] as string)
                            }
                            key={`cell-${cellIndex}`}
                            onMouseEnter={() => setFocusSeries(label.text)}
                            onMouseLeave={() => focusSeries && setFocusSeries(null)}
                          />
                        )
                      })}
                    </Bar>
                  )
                }
              })}
              <Scatter
                dataKey='seventyFivePercent'
                fill={thresholdColor}
                onMouseEnter={() => {
                  setFocusSeries('seventyFivePercent')
                }}
                onMouseLeave={() => {
                  setFocusSeries(null)
                }}
                shape={<ThresholdShape />}
              />
              <Scatter
                dataKey='eightyFivePercent'
                fill={thresholdColor}
                onMouseEnter={() => {
                  setFocusSeries('eightyFivePercent')
                }}
                onMouseLeave={() => {
                  setFocusSeries(null)
                }}
                shape={<ThresholdShape />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

const Availability = ({ limit, totalUsage }: { limit: number; totalUsage: number }) => {
  const available = limit - totalUsage
  if (available < 0) {
    return (
      <>
        {getThresholdIcon((totalUsage / limit) * 100, 'text-error-default')}
        <div className='text-error-default' data-testid='daily-credit-usage-limit-exceeded'>
          Daily limit exceeded by {getFormattedUsage(Math.abs(available))}
        </div>
      </>
    )
  }

  return (
    <>
      {getThresholdIcon((totalUsage / limit) * 100)}
      <span data-testid='daily-credit-usage-available'>{getFormattedUsage(limit - totalUsage)} available</span>
    </>
  )
}
